import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import * as api from "../services/api";
import Card from "../components/Card";
import Spinner from "../components/Spinner";
import Modal from "../components/Modal";
import Button from "../components/Button";
import Input from "../components/Input";
import { toast } from "react-toastify";
import { PlusCircleIcon, TrashIcon, CalculatorIcon, PencilIcon } from '@heroicons/react/24/outline';
import "react-toastify/dist/ReactToastify.css";
import { UserRole } from '../types'; 
import { useAuth } from '../hooks/useAuth'; 

const SCHOOL_CLASSES = [
  "الأول", "الثاني", "الثالث", "الرابع", "الخامس", "السادس",
  "السابع", "الثامن", "التاسع", "العاشر", "الحادي عشر"
];

// className → academic stage (matches the server's classStageMap so the
// auto-selected stage in the fee calculator reflects how the backend
// computes the student's tuition).
const CLASS_TO_STAGE = {
  "الأول": "الابتدائي",
  "الثاني": "الابتدائي",
  "الثالث": "الابتدائي",
  "الرابع": "الابتدائي",
  "الخامس": "الابتدائي",
  "السادس": "الابتدائي",
  "السابع": "الإعدادي",
  "الثامن": "الإعدادي",
  "التاسع": "الإعدادي",
  "العاشر": "الثانوي",
  "الحادي عشر": "الثانوي",
};

const fieldLabels = {
  serialNumber: "الرقم التسلسلي ",
  FirstName : "اسم التلميذ ",
  LastName: "اللقب ",
  FatherName: "اسم الأب ",
  GrandFatherName: "اسم الجد ",
  MotherName: "اسم الأم ",
  sex: "الجنس ",
  birthDate: "تاريخ الميلاد ",
  birthPlace: "مكان الولادة ",
  Registration: "محل ورقم القيد ",
  Nationality: "الجنسية ",
  Religion: "الديانة ",
  Language: "اللغة الأجنبية ",
  mobile:"رقم الموبايل ",
  phone:"رقم الأرضي ",
  HistoryOfRegistration: "تاريخ الانتساب للمدرسة ",
  previousSchool: "المدرسة التي انتقل منها ",
  NumberOfDocument: "رقم وثيقته عند الانتساب ",
  HistoryOfDocument: "تاريخ وثيقته عند الانتساب ",
  ClassOfRegistration: "الصف الذي انتسب اليه ",
  FailClass: "صفوف الرسوب السابقة ",
  notes: "ملاحظات ",
};
const hiddenFields = ["createdAt", "updatedAt", "__v", "_id"];

const formatValue = (value) => {
  if (!value) return "-";
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Date(value).toLocaleDateString('ar-SY');
  }
  return value;
};

const isoToInputDate = (isoString) => {
    if (!isoString) return '';
    try {
        // Handle potential invalid date strings safely
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
    } catch (e) { return ''; }
};

const getCurrentAcademicYear = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  const start = month >= 7 ? year : year - 1;
  return `${start}-${start + 1}`;
};

const InfoItem = ({ label, value }) => (
    <div className="p-2 border rounded-md bg-white">
        <span className="block text-xs font-medium text-gray-500">{label}</span>
        <span className="block text-sm font-semibold text-gray-800">{value || '-'}</span>
    </div>
);

/* ---------------- UpdateStudentModal (UNCHANGED) ---------------- */
const UpdateStudentModal = ({ student, isOpen, onClose, onUpdateSuccess }) => {
  const [formData, setFormData] = useState({});
  
  useEffect(() => {
    if (student && isOpen) {
        setFormData({
            ...student,
            birthDate: isoToInputDate(student.birthDate),
            HistoryOfRegistration: isoToInputDate(student.HistoryOfRegistration),
        });
    }
  }, [student, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dataToSubmit = { ...formData };
    delete dataToSubmit.serialNumber;
    delete dataToSubmit._id;

    try {
      await api.updateStudent(student.serialNumber, dataToSubmit);
      toast.success('تم تحديث بيانات الطالب بنجاح ✅');
      onUpdateSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'فشل في تحديث بيانات الطالب');
    }
  };

  const fieldsToRender = Object.keys(fieldLabels).filter(key => 
    !['serialNumber', 'notes'].includes(key) && !hiddenFields.includes(key)
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="✏️ تعديل بيانات الطالب">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label={fieldLabels.serialNumber} value={student.serialNumber} disabled readOnly />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fieldsToRender.map((key) => {
            const label = fieldLabels[key];
            const isDateField = key.toLowerCase().includes('date') || key.toLowerCase().includes('historyofregistration');
            return (
              <Input key={key} id={key} name={key} label={label} type={isDateField ? 'date' : 'text'} value={formData[key] || ''} onChange={handleChange} required={['FirstName', 'LastName', 'FatherName', 'MotherName', 'sex', 'birthDate', 'HistoryOfRegistration'].includes(key)} />
            );
          })}
        </div>
        <Input id="notes" name="notes" label={fieldLabels.notes} value={formData.notes || ''} onChange={handleChange} isTextArea={true} />
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>إلغاء</Button>
          <Button type="submit" className="bg-blue-600 hover:bg-blue-700">حفظ التعديلات</Button>
        </div>
      </form>
    </Modal>
  );
};

/* ---------------- AddTransferModal (UNCHANGED) ---------------- */
const AddTransferModal = ({ type = 'in', isOpen, onClose, onSuccess, student }) => {
  const [academicYear, setAcademicYear] = useState(getCurrentAcademicYear());
  const [province, setProvince] = useState('');
  const [notificationDate, setNotificationDate] = useState('');
  const [previousSchoolAddress, setPreviousSchoolAddress] = useState('');
  const [previousClass, setPreviousClass] = useState('');
  const [clearanceDoc, setClearanceDoc] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [newSchoolName, setNewSchoolName] = useState('');
  const [provinceOut, setProvinceOut] = useState('');
  const [notificationDoc, setNotificationDoc] = useState('');
  const [newSchoolAddress, setNewSchoolAddress] = useState('');

  useEffect(() => {
    if (isOpen) {
      setAcademicYear(getCurrentAcademicYear());
      setProvince(''); setNotificationDate(''); setPreviousSchoolAddress('');
      setPreviousClass(''); setClearanceDoc(''); setTransferDate(''); setNewSchoolName(''); setProvinceOut('');
      setNotificationDoc(''); setNewSchoolAddress('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!student?._id) { toast.error('لا يوجد طالب محدد'); return; }
    try {
      if (type === 'in') {
        await api.addTransferIn({ studentId: student._id, academicYear, province, notificationDate, previousSchoolAddress });
        toast.success('تم تسجيل النقل إلى المدرسة بنجاح');
      } else {
        await api.addTransferOut({ studentId: student._id, academicYear, previousClass, clearanceDoc, transferDate, newSchoolName, province: provinceOut, notificationDoc, newSchoolAddress });
        toast.success('تم تسجيل النقل من المدرسة بنجاح');
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'فشل في إضافة النقل');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={type === 'in' ? 'إضافة تحويل إلى المدرسة' : 'إضافة تحويل من المدرسة'}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="العام الدراسي" value={academicYear} disabled readOnly /> 
        {type === 'in' ? (
          <>
            <Input label="المحافظة التابعة لها المدرسة المنقول منها" value={province} onChange={(e) => setProvince(e.target.value)} required />
            <Input label="تاريخ إرسال وثيقة الاعلام بالدوام" type="date" value={notificationDate} onChange={(e) => setNotificationDate(e.target.value)} required />
            <Input label="عنوان المدرسة المنقول منها + رقم الهاتف" value={previousSchoolAddress} onChange={(e) => setPreviousSchoolAddress(e.target.value)} required />
          </>
        ) : (
          <>
            <Input label="الصف الذي انتقل منه" value={previousClass} onChange={(e) => setPreviousClass(e.target.value)} required />
            <Input label="رقم وتاريخ وثيقة اللامانع" value={clearanceDoc} onChange={(e) => setClearanceDoc(e.target.value)} required />
            <Input label="تاريخ النقل" type="date" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} required />
            <Input label="اسم المدرسة المنقول إليها" value={newSchoolName} onChange={(e) => setNewSchoolName(e.target.value)} required />
            <Input label="المحافظة التابعة لها" value={provinceOut} onChange={(e) => setProvinceOut(e.target.value)} required />
            <Input label="رقم وتاريخ وثيقة الاعلام بالدوام" value={notificationDoc} onChange={(e) => setNotificationDoc(e.target.value)} required />
            <Input label="عنوان المدرسة المنقول إليها + رقم الهاتف" value={newSchoolAddress} onChange={(e) => setNewSchoolAddress(e.target.value)} required />
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>إلغاء</Button>
          <Button type="submit">حفظ</Button>
        </div>
      </form>
    </Modal>
  );
};

/* ---------------- UpdateTransferInModal (UNCHANGED) ---------------- */
const UpdateTransferInModal = ({ isOpen, onClose, onSuccess, student, transferIn }) => {
  const [formData, setFormData] = useState({
    academicYear: '',
    province: '',
    notificationDate: '',
    previousSchoolAddress: ''
  });

  useEffect(() => {
    if (transferIn && isOpen) {
      setFormData({
        academicYear: transferIn.academicYear || getCurrentAcademicYear(),
        province: transferIn.transferIn?.province || '',
        notificationDate: isoToInputDate(transferIn.transferIn?.notificationDate),
        previousSchoolAddress: transferIn.transferIn?.previousSchoolAddress || ''
      });
    }
  }, [transferIn, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!student?._id) { toast.error('لا يوجد طالب محدد'); return; }
    try {
      await api.updateTransferIn(student._id, formData);
      toast.success('تم تحديث سجل النقل (دخول) بنجاح');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'فشل في تحديث النقل');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="تعديل سجل النقل إلى المدرسة">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="العام الدراسي" name="academicYear" value={formData.academicYear} onChange={handleChange} required />
        <Input label="المحافظة التابعة لها المدرسة المنقول منها" name="province" value={formData.province} onChange={handleChange} required />
        <Input label="تاريخ إرسال وثيقة الاعلام بالدوام" name="notificationDate" type="date" value={formData.notificationDate} onChange={handleChange} required />
        <Input label="عنوان المدرسة المنقول منها + رقم الهاتف" name="previousSchoolAddress" value={formData.previousSchoolAddress} onChange={handleChange} required />
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>إلغاء</Button>
          <Button type="submit" className="bg-blue-600 hover:bg-blue-700">حفظ التعديلات</Button>
        </div>
      </form>
    </Modal>
  );
};

/* ---------------- UpdateTransferOutModal (UNCHANGED) ---------------- */
const UpdateTransferOutModal = ({ isOpen, onClose, onSuccess, student, transferOut }) => {
  const [formData, setFormData] = useState({
    academicYear: '',
    previousClass: '',
    clearanceDoc: '',
    transferDate: '',
    newSchoolName: '',
    province: '',
    notificationDoc: '',
    newSchoolAddress: ''
  });

  useEffect(() => {
    if (transferOut && isOpen) {
      setFormData({
        academicYear: transferOut.academicYear || getCurrentAcademicYear(),
        previousClass: transferOut.transferOut?.previousClass || '',
        clearanceDoc: transferOut.transferOut?.clearanceDoc || '',
        transferDate: isoToInputDate(transferOut.transferOut?.transferDate),
        newSchoolName: transferOut.transferOut?.newSchoolName || '',
        province: transferOut.transferOut?.province || '',
        notificationDoc: transferOut.transferOut?.notificationDoc || '',
        newSchoolAddress: transferOut.transferOut?.newSchoolAddress || ''
      });
    }
  }, [transferOut, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!student?._id) { toast.error('لا يوجد طالب محدد'); return; }
    try {
      await api.updateTransferOut(student._id, formData);
      toast.success('تم تحديث سجل النقل (خروج) بنجاح');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'فشل في تحديث النقل');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="تعديل سجل النقل من المدرسة">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="العام الدراسي" name="academicYear" value={formData.academicYear} onChange={handleChange} required />
        <Input label="الصف الذي انتقل منه" name="previousClass" value={formData.previousClass} onChange={handleChange} required />
        <Input label="رقم وتاريخ وثيقة اللامانع" name="clearanceDoc" value={formData.clearanceDoc} onChange={handleChange} required />
        <Input label="تاريخ النقل" name="transferDate" type="date" value={formData.transferDate} onChange={handleChange} required />
        <Input label="اسم المدرسة المنقول إليها" name="newSchoolName" value={formData.newSchoolName} onChange={handleChange} required />
        <Input label="المحافظة التابعة لها" name="province" value={formData.province} onChange={handleChange} required />
        <Input label="رقم وتاريخ وثيقة الاعلام بالدوام" name="notificationDoc" value={formData.notificationDoc} onChange={handleChange} required />
        <Input label="عنوان المدرسة المنقول إليها + رقم الهاتف" name="newSchoolAddress" value={formData.newSchoolAddress} onChange={handleChange} required />
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>إلغاء</Button>
          <Button type="submit" className="bg-blue-600 hover:bg-blue-700">حفظ التعديلات</Button>
        </div>
      </form>
    </Modal>
  );
};


/* ---------------- OutOfSchoolConfirmationModal (UNCHANGED) ---------------- */
const OutOfSchoolConfirmationModal = ({ isOpen, onClose, data, student, onConfirm }) => (
    <Modal isOpen={isOpen} onClose={onClose} title="تأكيد تسجيل خروج طالب">
        <div className="space-y-4 text-right">
            <p className="font-semibold text-lg text-blue-700 border-b pb-2">بيانات الطالب</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoItem label="الرقم التسلسلي" value={student?.serialNumber} />
                <InfoItem label="الاسم الكامل" value={student ? `${student.FirstName} ${student.FatherName} ${student.LastName}` : ''} />
            </div>
            <p className="font-semibold text-lg text-blue-700 border-b pb-2 pt-4">نتائج الدراسة</p>
            <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                {data.studyingResults.map((r, i) => (
                    <div key={i} className="grid grid-cols-3 gap-2 p-2 bg-gray-50 rounded">
                        <InfoItem label="الصف" value={r.className} />
                        <InfoItem label="العام الدراسي" value={r.academicYear} />
                        <InfoItem label="النتيجة" value={r.result} />
                    </div>
                ))}
            </div>
            <p className="font-semibold text-lg text-blue-700 border-b pb-2 pt-4">تفاصيل الخروج</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <InfoItem label="الصف الذي تركه" value={data.classOfOut} />
                <InfoItem label="تاريخ تركه" value={formatValue(data.historyOfOut)} />
                <InfoItem label="الأسباب" value={data.reasons} />
            </div>
             <p className="font-semibold text-lg text-blue-700 border-b pb-2 pt-4">وثيقته عند الخروج</p>
             <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <InfoItem label="نوعها" value={data.documentOfOuting.type} />
                <InfoItem label="رقمها" value={data.documentOfOuting.number} />
                <InfoItem label="تاريخها" value={formatValue(data.documentOfOuting.history)} />
            </div>
            <p className="text-red-600 text-sm pt-4">يرجى التأكد من صحة جميع البيانات قبل الحفظ النهائي.</p>
        </div>
        <div className="flex justify-end gap-2 pt-6 border-t mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>تعديل</Button>
            <Button type="button" onClick={onConfirm} className="bg-green-600 hover:bg-green-700">تأكيد وحفظ</Button>
        </div>
    </Modal>
);

/* ---------------- (معدل) OutOfSchoolRecordModal ---------------- */
const OutOfSchoolRecordModal = ({ isOpen, onClose, onSuccess, student, isEditing = false, record = null }) => {
    const getInitialState = () => ({
        studyingResults: [{ className: SCHOOL_CLASSES[0], academicYear: getCurrentAcademicYear(), result: '' }],
        classOfOut: SCHOOL_CLASSES[0],
        historyOfOut: '',
        reasons: '',
        documentOfOuting: { type: '', number: '', history: '' }
    });

    const [formData, setFormData] = useState(getInitialState());
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (isEditing && record) {
                setFormData({
                    studyingResults: record.studyingResults?.length > 0 ? record.studyingResults : [{ className: SCHOOL_CLASSES[0], academicYear: getCurrentAcademicYear(), result: '' }],
                    classOfOut: record.classOfOut || SCHOOL_CLASSES[0],
                    historyOfOut: isoToInputDate(record.historyOfOut),
                    reasons: record.reasons || '',
                    documentOfOuting: {
                        type: record.documentOfOuting?.type || '',
                        number: record.documentOfOuting?.number || '',
                        history: isoToInputDate(record.documentOfOuting?.history)
                    }
                });
            } else {
                setFormData(getInitialState());
            }
            setIsConfirmationOpen(false);
        }
    }, [isOpen, isEditing, record]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleDocumentChange = (e) => setFormData(prev => ({ ...prev, documentOfOuting: { ...prev.documentOfOuting, [e.target.name]: e.target.value } }));
    const handleResultChange = (index, e) => {
        const updatedResults = [...formData.studyingResults];
        updatedResults[index][e.target.name] = e.target.value;
        setFormData(prev => ({ ...prev, studyingResults: updatedResults }));
    };
    const addResult = () => setFormData(prev => ({ ...prev, studyingResults: [...prev.studyingResults, { className: SCHOOL_CLASSES[0], academicYear: getCurrentAcademicYear(), result: '' }] }));
    const removeResult = (index) => setFormData(prev => ({ ...prev, studyingResults: prev.studyingResults.filter((_, i) => i !== index) }));

    const handleReview = (e) => {
        e.preventDefault();
        if (!student) { toast.error('بيانات الطالب غير متوفرة.'); return; }
        if (!formData.classOfOut || !formData.historyOfOut || !formData.reasons) { toast.error('الرجاء تعبئة حقول الخروج المطلوبة.'); return; }
        if (formData.studyingResults.some(r => !r.className || !r.academicYear || !r.result)) { toast.error('الرجاء تعبئة جميع حقول نتائج الدراسة.'); return; }
        setIsConfirmationOpen(true);
    };
    
    const handleConfirmSave = async () => {
      const payload = {
        ...formData,
        documentOfOuting: { ...formData.documentOfOuting, history: formData.documentOfOuting.history || null }
      };

      try {
        if (isEditing) {
          await api.updateOutOfSchoolRecord(student.serialNumber, payload);
          toast.success("تم تحديث سجل الخروج بنجاح.");
        } else {
          payload.studentId = student.serialNumber;
          await api.addOutOfSchoolRecord(payload);
          toast.success("تم تسجيل خروج الطالب بنجاح.");
        }
        onSuccess();
        onClose();
      } catch (err) {
        toast.error(err.response?.data?.message || (isEditing ? 'فشل تحديث السجل' : 'فشل تسجيل الخروج'));
      } finally {
        setIsConfirmationOpen(false);
      }
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? "تعديل سجل خروج الطالب" : "إضافة خروج طالب من المدرسة"}>
                <form onSubmit={handleReview} className="space-y-6">
                     <div className="p-2 bg-gray-100 rounded-md border"><div className="text-md text-gray-800">الطالب: <strong>{student?.serialNumber} — {student?.FirstName} {student?.FatherName} {student?.LastName}</strong></div></div>
                    <div className="border-t pt-4">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">نتائج الدراسة</h3>
                        {formData.studyingResults.map((result, index) => (
                            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center mb-3 p-2 border rounded-md">
                                 <select name="className" value={result.className} onChange={(e) => handleResultChange(index, e)} className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white focus:ring-2 focus:ring-blue-500">
                                    {SCHOOL_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <Input name="academicYear" placeholder="العام الدراسي" value={result.academicYear} onChange={(e) => handleResultChange(index, e)} required />
                                <Input name="result" placeholder="النتيجة" value={result.result} onChange={(e) => handleResultChange(index, e)} required />
                                <Button type="button" variant="danger" onClick={() => removeResult(index)} className="flex items-center justify-center gap-1 h-10"><TrashIcon className="h-4 w-4" /> حذف</Button>
                            </div>
                        ))}
                        <Button type="button" variant="ghost" onClick={addResult} className="flex items-center gap-1 text-blue-600"><PlusCircleIcon className="h-5 w-5" /> إضافة نتيجة</Button>
                    </div>
                     <div className="border-t pt-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">تفاصيل الخروج</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">الصف الذي تركه</label>
                                <select name="classOfOut" value={formData.classOfOut} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white focus:ring-2 focus:ring-blue-500">
                                    {SCHOOL_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <Input label="تاريخ تركه" name="historyOfOut" type="date" value={formData.historyOfOut} onChange={handleChange} required />
                            <Input label="الأسباب" name="reasons" value={formData.reasons} onChange={handleChange} required className="md:col-span-3"/>
                        </div>
                    </div>
                     <div className="border-t pt-4">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">وثيقته عند الخروج (اختياري)</h3>
                         <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                           <Input label="نوعها" name="type" value={formData.documentOfOuting.type} onChange={handleDocumentChange} />
                           <Input label="رقمها" name="number" value={formData.documentOfOuting.number} onChange={handleDocumentChange} />
                           <Input label="تاريخها" name="history" type="date" value={formData.documentOfOuting.history} onChange={handleDocumentChange} />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-6 border-t">
                      <Button type="button" variant="secondary" onClick={onClose}>إلغاء</Button>
                      <Button type="submit">مراجعة وحفظ</Button>
                    </div>
                </form>
            </Modal>
            <OutOfSchoolConfirmationModal 
              isOpen={isConfirmationOpen} 
              onClose={() => setIsConfirmationOpen(false)} 
              data={formData} 
              student={student} 
              onConfirm={handleConfirmSave} 
            />
        </>
    );
};
/* ---------------- FeeCalculatorModal (UPDATED: discount is absolute amount) ---------------- */
const FeeCalculatorModal = ({
    isOpen,
    onClose,
    schoolFees,
    totalPaid,
    studentDiscount = 0,
    studentDiscountType = "fixed",
    defaultClassState = "الابتدائي",
}) => {
    const [selectedClassState, setSelectedClassState] = useState(defaultClassState);
    const [includeBus, setIncludeBus] = useState(false);

    // Sync the auto-detected stage when the modal is (re-)opened, so reopening
    // after switching academic year keeps the selection in sync with the
    // student's enrollment for that year.
    useEffect(() => {
        if (isOpen) setSelectedClassState(defaultClassState);
    }, [isOpen, defaultClassState]);

    if (!schoolFees) {
        return (
            <Modal isOpen={isOpen} onClose={onClose} title="حساب الرسوم المتبقية">
                <div className="text-center p-4">
                    <Spinner />
                    <p className="mt-2">إذا لم يظهر المحتوى فهذا يعني عدم تحديد رسوم المراحل لهذا العام</p>
                </div>
            </Modal>
        );
    }

    // Base tuition for selected stage
    const baseTuition = Number(schoolFees[selectedClassState]?.tuition || 0);

    // Discount: either a fixed L.S amount or a percentage of the base tuition
    const discountIsPercent = studentDiscountType === "percentage";
    const discountAmount = discountIsPercent
        ? (baseTuition * Number(studentDiscount || 0)) / 100
        : Number(studentDiscount || 0);

    // Tuition after the resolved discount
    const tuitionAfterDiscount = Math.max(0, baseTuition - discountAmount);

    // Bus fee logic
    const stageBusFee = Number(schoolFees[selectedClassState]?.busFee || 0);
    const busFee = includeBus ? stageBusFee : 0;

    // Total & remainder
    const totalRequired = tuitionAfterDiscount + busFee;
    const remainder = totalRequired - totalPaid;

    const formatCurrency = (n) => `${Number(n).toLocaleString()} ل.س`;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="حساب الرسوم المتبقية">
            <div className="space-y-6">

                {/* ----- Inputs Section ----- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg bg-gray-50">
                    
                    {/* Stage selector */}
                    <div>
                        <label className="block text-sm font-medium mb-1">المرحلة الدراسية</label>
                        <select
                            value={selectedClassState}
                            onChange={(e) => setSelectedClassState(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg bg-white"
                        >
                            <option value="الابتدائي">الابتدائي</option>
                            <option value="الإعدادي">الإعدادي</option>
                            <option value="الثانوي">الثانوي</option>
                        </select>
                    </div>

                    {/* Show discount — either an absolute L.S amount or
                        the percentage value (e.g. "25 %"). */}
                    <div>
                        <Input
                            label={discountIsPercent ? "قيمة الخصم (٪)" : "قيمة الخصم (ل.س)"}
                            value={
                                discountIsPercent
                                    ? `${Number(studentDiscount || 0)} %`
                                    : Number(studentDiscount || 0)
                            }
                            disabled
                        />
                    </div>

                    <div className="md:col-span-2 flex items-center justify-center gap-3 pt-3">
                        <input
                            type="checkbox"
                            id="includeBus"
                            checked={includeBus}
                            onChange={(e) => setIncludeBus(e.target.checked)}
                            className="h-5 w-5"
                        />
                        <label htmlFor="includeBus" className="text-md font-medium">
                            تضمين رسوم النقل ؟
                        </label>
                    </div>
                </div>

                {/* ----- Calculation Section ----- */}
                <div className="space-y-3 p-4 border-t pt-6">
                    
                    <div className="flex justify-between text-lg">
                        <span className="font-medium">القسط الأساسي:</span>
                        <span className="font-semibold">{formatCurrency(baseTuition)}</span>
                    </div>

                    <div className="flex justify-between text-lg text-red-600">
                        <span className="font-medium">الخصم:</span>
                        <span className="font-semibold">-{formatCurrency(discountAmount)}</span>
                    </div>

                    <div className="flex justify-between text-lg">
                        <span className="font-medium">رسوم النقل:</span>
                        <span className="font-semibold">{formatCurrency(busFee)}</span>
                    </div>

                    <hr className="my-3" />

                    <div className="flex justify-between text-xl font-bold p-2 bg-blue-50 rounded-lg">
                        <span className="text-blue-900">الإجمالي المطلوب:</span>
                        <span className="text-blue-900">{formatCurrency(totalRequired)}</span>
                    </div>

                    <div className="flex justify-between text-xl font-bold p-2 bg-green-50 rounded-lg">
                        <span className="text-green-900">المدفوع:</span>
                        <span className="text-green-900">{formatCurrency(totalPaid)}</span>
                    </div>

                    <hr className="my-3" />

                    <div className={`flex justify-between text-3xl font-bold p-3 rounded-lg ${remainder > 0 ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}`}>
                        <span>المتبقي:</span>
                        <span>{formatCurrency(remainder)}</span>
                    </div>

                </div>

                <div className="flex justify-end pt-4 border-t">
                    <Button variant="secondary" onClick={onClose}>
                        إغلاق
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

/* ---------------- StudentDetailPage (Component) ---------------- */
const StudentDetailPage = () => {
  const { serialNumber } = useParams();
  const auth = useAuth();
  const role = auth?.user?.role || auth?.role || (Array.isArray(auth?.user?.roles) ? auth.user.roles[0] : null);

  const isAccountant = role === UserRole.ACCOUNTANT;
  const isSecretary = role === UserRole.SECRETARY;
  const isOwner = role === UserRole.OWNER;

  const [student, setStudent] = useState(null);
  const [classGroups, setClassGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [transfersIn, setTransfersIn] = useState([]);
  const [transfersOut, setTransfersOut] = useState([]);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [isAddTransferOpen, setIsAddTransferOpen] = useState(false);
  const [addTransferType, setAddTransferType] = useState('in');
  const [academicYear, setAcademicYear] = useState(getCurrentAcademicYear());
  const [amount, setAmount] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [fees, setFees] = useState(null);
  const [feesLoading, setFeesLoading] = useState(false);
  
  const [outOfSchoolRecord, setOutOfSchoolRecord] = useState(null);
  const [outOfSchoolLoading, setOutOfSchoolLoading] = useState(true);
  
  const [isOutOfSchoolModalOpen, setIsOutOfSchoolModalOpen] = useState(false);
  const [isEditingOutOfSchool, setIsEditingOutOfSchool] = useState(false);
  
  const [isCalculatorModalOpen, setIsCalculatorModalOpen] = useState(false);
  const [allSchoolFees, setAllSchoolFees] = useState(null);
  const [schoolFeesLoading, setSchoolFeesLoading] = useState(true);
  
  const [isUpdateTransferInOpen, setIsUpdateTransferInOpen] = useState(false);
  const [isUpdateTransferOutOpen, setIsUpdateTransferOutOpen] = useState(false);

  // Discount modal state & studentDiscount state
  const [isAddDiscountOpen, setIsAddDiscountOpen] = useState(false);
  const [newDiscountValue, setNewDiscountValue] = useState("");
  const [newDiscountType, setNewDiscountType] = useState("fixed"); // "fixed" | "percentage"
  const [studentDiscount, setStudentDiscount] = useState(0);
  const [studentDiscountType, setStudentDiscountType] = useState("fixed");

  // Fixed dropdown options: 2025-2026 .. 2035-2036
  const ACADEMIC_YEAR_OPTIONS = [];
  for (let start = 2025; start <= 2035; start++) {
    ACADEMIC_YEAR_OPTIONS.push(`${start}-${start + 1}`);
  }

  const fetchDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.getStudentDetails(serialNumber);
      setStudent(data.student || data); 
      setClassGroups(data.classGroups || []);
      // Keep page default to current academic year
      const year = getCurrentAcademicYear();
      setAcademicYear(year);
      // fetch fees for current year immediately
      setFeesLoading(true);
      try {
        const res = await api.getStudentFees(serialNumber, year);
        setFees(res.data);
      } catch {
        setFees({ transactions: [], totalPaid: 0, academicYear: year });
      }
    } catch (err) {
      console.error(err);
      setError('فشل في جلب بيانات الطالب.');
    } finally {
      setLoading(false);
      setFeesLoading(false);
    }
  };

  const fetchSchoolFees = async (yearToUse) => {
    setSchoolFeesLoading(true);
    try {
      const year = yearToUse || academicYear || getCurrentAcademicYear();
      const data = await api.getSchoolFees(year);
      setAllSchoolFees(data);
    } catch (err) {
        console.error("فشل في جلب الرسوم المدرسية:", err);
        toast.error("لم يتم تحديد رسوم المراحل الدراسية بعد لهذا العام الدراسي");
        setAllSchoolFees(null);
    } finally {
        setSchoolFeesLoading(false);
    }
  };

  const fetchStudentFeesByYear = async (yearToUse) => {
    if (!serialNumber || !yearToUse) return;
    setFeesLoading(true);
    try {
      const res = await api.getStudentFees(serialNumber, yearToUse);
      setFees(res.data);
    } catch (err) {
      // If no payments found, show empty structure rather than crashing
      console.warn("No payments for student for year:", yearToUse, err?.response?.data?.error);
      setFees({ transactions: [], totalPaid: 0, academicYear: yearToUse });
    } finally {
      setFeesLoading(false);
    }
  };

const fetchStudentDiscount = async (yearToUse) => {
  if (!student?.serialNumber) return; // wait until student loads

  try {
    const res = await api.getStudentDiscount(student.serialNumber, yearToUse);
    const d = res?.data?.discount ?? 0;
    const t = res?.data?.type ?? "fixed";
    setStudentDiscount(Number(d));
    setStudentDiscountType(t === "percentage" ? "percentage" : "fixed");
  } catch (err) {
    console.warn("No discount found:", err);
    setStudentDiscount(0);
    setStudentDiscountType("fixed");
  }
};



  const fetchTransfersForStudent = async (studentId) => {
    setTransfersLoading(true);
    try {
      const { data } = await api.getTransfersByStudent(studentId);
      setTransfersIn(data.filter(t => t.type === 'in'));
      setTransfersOut(data.filter(t => t.type === 'out'));
    } catch (err) {
      console.error(err);
      toast.error('فشل في جلب سجلات النقل');
    } finally {
      setTransfersLoading(false);
    }
  };

  const fetchOutOfSchoolDetails = async (serialNum) => {
    setOutOfSchoolLoading(true);
    try {
        const { data } = await api.getOutOfSchoolRecordByStudent(serialNum);
        setOutOfSchoolRecord(data);
    } catch (err) {
        console.log("No out-of-school record found for this student.");
        setOutOfSchoolRecord(null);
    } finally {
        setOutOfSchoolLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();

    // only fetch school-fees if the user will see/use them (initial fetch)
    if (isOwner || isAccountant) {
      // fetchSchoolFees will be re-run whenever academicYear changes thanks to other effect below
      fetchSchoolFees();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNumber, isOwner, isAccountant]);

  useEffect(() => {
    if (student?._id) {
      fetchTransfersForStudent(student._id);
      fetchOutOfSchoolDetails(student.serialNumber);
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student]);

  // Whenever academicYear changes (user selected different year) we need to:
  // - fetch student fees for that year
  // - fetch school fees for that year (used by FeeCalculatorModal / getSchoolFees)
  // - fetch student discount for that year
useEffect(() => {
    if (!student?._id || !academicYear) return;

    fetchStudentFeesByYear(academicYear);

    if (isOwner || isAccountant) {
        fetchSchoolFees(academicYear);
    }

    fetchStudentDiscount(academicYear);

}, [academicYear, student?._id, isOwner, isAccountant]);


  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      await api.addPaymentBySerial(serialNumber, { academicYear, amount: Number(amount), receiptNumber, date, notes });
      toast.success('تم تسجيل الدفع بنجاح ✅');
      const newTotal = (fees?.totalPaid || 0) + Number(amount);
      const newTx = { _id: Date.now(), amount: Number(amount), receiptNumber, date, notes };
      setFees(prev => ({ ...prev, transactions: [...(prev?.transactions || []), newTx], totalPaid: newTotal }));
      setIsPaymentModalOpen(false);
      setAmount(''); setReceiptNumber(''); setDate(''); setNotes('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'فشل في إضافة الدفع');
    }
  };

  const openAddTransfer = (type) => {
    if (!student) { toast.error('يرجى انتظار تحميل بيانات الطالب أولاً.'); return; }
    setAddTransferType(type);
    setIsAddTransferOpen(true);
  };

  const onTransferAdded = () => { if (student?._id) fetchTransfersForStudent(student._id); };
  const onTransferUpdated = () => { if (student?._id) fetchTransfersForStudent(student._id); };
  const onStudentUpdated = () => fetchDetails();
  
  const onOutOfSchoolSuccess = () => { if (student) fetchOutOfSchoolDetails(student.serialNumber); };

  if (loading) return <div className="flex justify-center items-center h-64"><Spinner /></div>;
  if (error) return <p className="text-red-500">{error}</p>;
  if (!student) return <p>لم يتم العثور على الطالب.</p>;

  const totalPaidForCalculator = fees?.totalPaid || 0;

  /* ---------- Discount modal handlers ---------- */
  const openAddDiscountModal = () => {
    setNewDiscountValue(studentDiscount ? String(studentDiscount) : "");
    setNewDiscountType(studentDiscountType || "fixed");
    setIsAddDiscountOpen(true);
  };

  const closeAddDiscountModal = () => {
    setIsAddDiscountOpen(false);
  };

  const handleSaveDiscount = async (e) => {
    e.preventDefault();
    if (!student || !academicYear) {
      toast.error("لا يمكن تحديد الخصم الآن. تأكد من تحميل بيانات الطالب واختيار العام الدراسي.");
      return;
    }
    const parsed = Number(newDiscountValue);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error("قيمة الخصم غير صالحة.");
      return;
    }
    if (newDiscountType === "percentage" && parsed > 100) {
      toast.error("النسبة المئوية يجب أن تكون بين 0 و 100.");
      return;
    }
    try {
      await api.setStudentDiscount(student.serialNumber, {
        academicYear,
        discount: parsed,
        type: newDiscountType,
      });
      toast.success("تم حفظ الخصم.");
      setStudentDiscount(parsed);
      setStudentDiscountType(newDiscountType);
      setIsAddDiscountOpen(false);
    } catch (err) {
      console.error("save discount:", err);
      toast.error(err?.response?.data?.error || "فشل في حفظ الخصم");
    }
  };

  // The class group the student is enrolled in for the currently selected
  // academic year, if any. Used to auto-select the academic stage in the
  // remainder calculator.
  const enrolledForYear = classGroups.find(
    (g) => g.academicYear === academicYear
  );
  const autoStage =
    (enrolledForYear && CLASS_TO_STAGE[enrolledForYear.className]) ||
    "الابتدائي";

  return (
    <div className="space-y-6">
      {/* ----------------- الرسوم الدراسية -----------------
          Rendered only for owner OR accountant per spec.
          Placed before the student profile card per spec. */}
      {(isOwner || isAccountant) && (
        <Card title="- الرسوم الدراسية">
          {/* NEW: Academic year selector (fixed range) */}
          <div className="mb-4 flex items-center gap-4">
            <label className="font-medium">اختر العام الدراسي :</label>
            <select
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              className="px-3 py-2 border rounded-lg bg-white"
            >
              {/* Ensure the dropdown includes fixed list 2025-2026 .. 2035-2036 */}
              {ACADEMIC_YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {feesLoading ? <Spinner /> : fees && fees.transactions.length > 0 ? (
            <div>
              <h4 className="font-semibold text-slate-1000 mb-2">العام الدراسي :  <span className="text-blue-600">{fees.academicYear}</span></h4>
              <table className="w-full text-lg border">
                <thead><tr className="text-slate-1000"><th className="p-2 border">المبلغ</th><th className="p-2 border">رقم الإيصال</th><th className="p-2 border">التاريخ</th><th className="p-2 border">ملاحظات</th></tr></thead>
                <tbody>{fees.transactions.map((tx, index) => (<tr key={tx._id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}><td className="border p-2 text-center">{tx.amount.toLocaleString()}</td><td className="border p-2 text-center">{tx.receiptNumber}</td><td className="border p-2 text-center">{formatValue(tx.date)}</td><td className="border p-2 text-center">{tx.notes || '-'}</td></tr>))}</tbody>
              </table>
              <p className="mt-2 font-semibold text-lg text-green-700">المجموع المدفوع: {fees.totalPaid.toLocaleString()} ل.س</p>
            </div>
          ) : <p className="text-slate-900 text-md">لا يوجد أقساط مدفوعة لهذا العام الدراسي.</p>}
          
          <div className="mt-4 pt-4 border-t flex justify-start items-center gap-3">
              <Button 
                  onClick={() => setIsCalculatorModalOpen(true)} 
                  className="bg-yellow-500 hover:bg-yellow-600 text-yellow-900"
                  disabled={schoolFeesLoading}
              >
                  <CalculatorIcon className="h-5 w-5 ml-2" />
                  {schoolFeesLoading ? "جاري تحميل الرسوم..." : "احسب المتبقي"}
              </Button>

              {/* NEW: Add Discount button — uses current academicYear automatically */}
              <Button
                onClick={openAddDiscountModal}
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
                disabled={!academicYear || !student}
              >
                إضافة خصم
              </Button>

              {/* show current discount value (read-only small info) */}
              <div className="text-md text-slate-900 ml-3">
                {studentDiscount
                  ? `الخصم الحالي: ${studentDiscount.toLocaleString()} ${
                      studentDiscountType === "percentage" ? "%" : "ل.س"
                    }`
                  : "لا يوجد خصم محدد"}
              </div>
          </div>
        </Card>
      )}

      {/* ----------------- ملف الطالب ----------------- */}
      <Card title={` - ملف الطالب:  ${student.FirstName} ${student.FatherName} ${student.LastName}`}>
        <div className="text-lg font-bold text-black-5000 mb-9 ">{fieldLabels.serialNumber}: <span className="text-blue-600">{student.serialNumber}</span></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.keys(fieldLabels).map((key) => {
            if (hiddenFields.includes(key) || key === 'serialNumber') return null;
            return (
              <div key={key} className="py-2">
                <span className="font-semibold text-black-7000 text-lg">{fieldLabels[key]}:</span>
                <span className="mr-2 text-black-4000  text-xl">{formatValue(student[key])}</span>
              </div>
            );
           })}
        </div>

        {/* Buttons: Edit visible for owner & secretary.
            Pay visible for owner & accountant (secretary must NOT see pay). */}
        <div className="mt-4 flex justify-end gap-2">
          {(isOwner || isSecretary) && (
            <Button onClick={() => setIsUpdateModalOpen(true)} className="bg-blue-600 hover:bg-blue-700">✏️ تعديل بيانات الطالب</Button>
          )}
          {(isOwner || isAccountant) && (
            <Button onClick={() => setIsPaymentModalOpen(true)} className="bg-green-600 hover:bg-green-700">💰 تسديد قسط</Button>
          )}
        </div>
      </Card>

      {/* ----------------- السجل الدراسي ----------------- (unchanged rest of file) */}
      {(isOwner || isSecretary) && (
        <>
          <Card title="- السجل الدراسي">
            {classGroups.length > 0 ? (<ul className="space-y-2">{classGroups.map((group) => (<li key={group._id} className="p-3 bg-gray-50 rounded-md"><Link to={`/classes/${group._id}`}   title="اضغط للإنتقال إلى صفحة الشعبة  "
                className="font-semibold text-blue-600 hover:underline">{group.className} - {group.roomNumber}</Link><span className="text-slate-900 mr-4">({group.academicYear})</span></li>))}</ul>) : <p>لا يوجد سجل دراسي لهذا الطالب.</p>}
          </Card>

          <Card title= " -  سجل النقل إلى المدرسة الخاص بالطالب  ">
            <div className="flex justify-end mb-4">
              {transfersIn.length === 0 ? (
                <Button onClick={() => openAddTransfer('in')} className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2">
                  <PlusCircleIcon className="h-5 w-5" />
                  إضافة إلى السجل
                </Button>
              ) : (
                <Button onClick={() => setIsUpdateTransferInOpen(true)} className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2">
                  <PencilIcon className="h-5 w-5" />
                  تعديل بيانات النقل
                </Button>
              )}
            </div>
            
            {transfersLoading ? <Spinner /> : transfersIn.length > 0 ? (<div className="overflow-x-auto"><table className="min-w-full border-collapse border border-gray-300"><thead className="text-lg text-gray-1000"><tr><th className="p-2 border">العام الدراسي</th><th className="p-2 border">المحافظة المنقول منها</th><th className="p-2 border">تاريخ الاعلام بالدوام</th><th className="p-2 border">عنوان المدرسة المنقول منها</th></tr></thead><tbody>{transfersIn.map((t, index) => (<tr key={t._id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}><td className="border p-2 text-center">{t.academicYear}</td><td className="border p-2 text-center">{t.transferIn?.province || '-'}</td><td className="border p-2 text-center">{formatValue(t.transferIn?.notificationDate) || '-'}</td><td className="border p-2 text-center">{t.transferIn?.previousSchoolAddress || '-'}</td></tr>))}</tbody></table></div>) : <p className="text-gray-500 p-4">لا توجد سجلات تحويل دخول لهذا الطالب.</p>}
          </Card>

          <Card title="- سجل النقل من المدرسة الخاص بالطالب  ">
            <div className="flex justify-end mb-4">
              {transfersOut.length === 0 ? (
                <Button onClick={() => openAddTransfer('out')} className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2">
                  <PlusCircleIcon className="h-5 w-5" />
                  إضافة إلى السجل
                </Button>
              ) : (
                <Button onClick={() => setIsUpdateTransferOutOpen(true)} className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2">
                  <PencilIcon className="h-5 w-5" />
                  تعديل بيانات النقل
                </Button>
              )}
            </div>

            {transfersLoading ? <Spinner /> : transfersOut.length > 0 ? (<div className="overflow-x-auto"><table className="min-w-full border-collapse border border-gray-300"><thead className="text-lg text-gray-1000"><tr><th className="p-2 border">العام الدراسي</th><th className="p-2 border">الصف المنتقل منه</th><th className="p-2 border">رقم وتاريخ اللامانع</th><th className="p-2 border">تاريخ النقل</th><th className="p-2 border">المدرسة المنقول إليها</th><th className="p-2 border">المحافظة</th><th className="p-2 border">رقم وثيقة الإعلام بالدوام</th><th className="p-2 border">عنوان المدرسة المنقول إليها</th></tr></thead><tbody>{transfersOut.map((t, index) => (<tr key={t._id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}><td className="border p-2 text-center">{t.academicYear}</td><td className="border p-2 text-center">{t.transferOut?.previousClass || '-'}</td><td className="border p-2 text-center">{t.transferOut?.clearanceDoc || '-'}</td><td className="border p-2 text-center">{formatValue(t.transferOut?.transferDate) || '-'}</td><td className="border p-2 text-center">{t.transferOut?.newSchoolName || '-'}</td><td className="border p-2 text-center">{t.transferOut?.province || '-'}</td><td className="border p-2 text-center">{t.transferOut?.notificationDoc || '-'}</td><td className="border p-2 text-center">{t.transferOut?.newSchoolAddress || '-'}</td></tr>))}</tbody></table></div>) : <p className="text-gray-500 p-4">لا توجد سجلات تحويل خروج لهذا الطالب.</p>}
          </Card>
          
          <Card title="- سجل الخروج من المدرسة الخاص بالطالب">
            {outOfSchoolLoading ? <Spinner /> : outOfSchoolRecord ? (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 p-4">
                      <div className="space-y-1 text-sm">
                          <h4 className="font-bold text-gray-700 border-b pb-1 mb-2 text-center">نتائج الدراسة</h4>
                          <div className="max-h-40 overflow-auto">
                              <table className="min-w-full text-xs"><thead className="bg-gray-100"><tr><th className="p-1 text-right">اسم الصف</th><th className="p-1 text-right">العام الدراسي</th><th className="p-1 text-right">النتيجة</th></tr></thead>
                                  <tbody>
                                      {outOfSchoolRecord.studyingResults.map((res, idx) => (
                                          <tr key={idx} className="border-b"><td className="p-1">{res.className}</td><td className="p-1">{res.academicYear}</td><td className="p-1 font-semibold">{res.result}</td></tr>
                                      ))} 
                                  </tbody>
                              </table>
                          </div>
                      </div>
                      <div className="space-y-2 text-sm">
                          <h4 className="font-bold text-slate-700 border-b pb-1 mb-2 text-center">الخروج من المدرسة</h4>
                          <div className="flex flex-col md:flex-row justify-between items-start gap-x-4">
                              <div className="space-y-1 mb-2 md:mb-0">
                                  <p><span className="font-semibold">الصف الذي تركه:</span> {outOfSchoolRecord.classOfOut}</p>
                                  <p><span className="font-semibold">تاريخ تركه:</span> {formatValue(outOfSchoolRecord.historyOfOut)}</p>
                                  <p><span className="font-semibold">الأسباب:</span> {outOfSchoolRecord.reasons}</p>
                              </div>
                              <div>
                                  {outOfSchoolRecord.documentOfOuting && (outOfSchoolRecord.documentOfOuting.type || outOfSchoolRecord.documentOfOuting.number || outOfSchoolRecord.documentOfOuting.history) && (
                                      <div>
                                          <h5 className="font-semibold text-gray-600">وثيقته عند الخروج:</h5>
                                          <p className="pl-2"><span className="font-semibold">نوعها:</span> {outOfSchoolRecord.documentOfOuting.type || '-'}</p>
                                          <p className="pl-2"><span className="font-semibold">رقمها:</span> {outOfSchoolRecord.documentOfOuting.number || '-'}</p>
                                          <p className="pl-2"><span className="font-semibold">تاريخها:</span> {formatValue(outOfSchoolRecord.documentOfOuting.history)}</p>
                                      </div>
                                  )}
                              </div>
                          </div>
                      </div>
                  </div>
                  <div className="flex justify-end p-4 border-t mt-2">
                    <Button 
                      onClick={() => {
                        setIsEditingOutOfSchool(true);
                        setIsOutOfSchoolModalOpen(true);
                      }} 
                      className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                    >
                      <PencilIcon className="h-5 w-5" />
                      تعديل سجل الخروج
                    </Button>
                  </div>
                </>
            ) : (
                <div className="p-4 text-center">
                    <p className="text-gray-500 mb-4">لا يوجد سجل خروج لهذا الطالب.</p>
                    <Button 
                      onClick={() => {
                        setIsEditingOutOfSchool(false);
                        setIsOutOfSchoolModalOpen(true);
                      }} 
                      className="bg-orange-600 hover:bg-orange-700 text-white flex items-center gap-2"
                    >
                      <PlusCircleIcon className="h-5 w-5" />
                      إضافة سجل خروج
                    </Button>
                </div>
            )}
          </Card>
        </>
      )}

      {/* --- (Modals) --- */}
      <Modal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} title="💰 إضافة تسديد قسط">
        <form onSubmit={handlePayment} className="space-y-4">
          <Input label="العام الدراسي" value={academicYear} disabled readOnly />
          <Input label="المبلغ" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          <Input label="رقم الإيصال" value={receiptNumber} onChange={(e) => setReceiptNumber(e.target.value)} required />
          <Input label="التاريخ" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <Input label="ملاحظات" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => setIsPaymentModalOpen(false)}>إلغاء</Button><Button type="submit" className="bg-green-600 hover:bg-green-700">حفظ</Button></div>
        </form>
      </Modal>

      {/* Update student modal should only be reachable if user had edit button visible */}
      <UpdateStudentModal student={student} isOpen={isUpdateModalOpen} onClose={() => setIsUpdateModalOpen(false)} onUpdateSuccess={onStudentUpdated} />
      <AddTransferModal type={addTransferType} isOpen={isAddTransferOpen} onClose={() => setIsAddTransferOpen(false)} onSuccess={onTransferAdded} student={student} />

      <UpdateTransferInModal 
        isOpen={isUpdateTransferInOpen} 
        onClose={() => setIsUpdateTransferInOpen(false)} 
        onSuccess={onTransferUpdated} 
        student={student} 
        transferIn={transfersIn[0]}
      />
      <UpdateTransferOutModal 
        isOpen={isUpdateTransferOutOpen} 
        onClose={() => setIsUpdateTransferOutOpen(false)} 
        onSuccess={onTransferUpdated} 
        student={student} 
        transferOut={transfersOut[0]}
      />

      <OutOfSchoolRecordModal 
        student={student} 
        isOpen={isOutOfSchoolModalOpen} 
        onClose={() => setIsOutOfSchoolModalOpen(false)} 
        onSuccess={onOutOfSchoolSuccess} 
        isEditing={isEditingOutOfSchool} 
        record={outOfSchoolRecord} 
      />

      {/* --- Add Discount Modal (minimal, same styling as other modals) --- */}
      <Modal isOpen={isAddDiscountOpen} onClose={closeAddDiscountModal} title="إضافة / تعديل خصم">
        <form onSubmit={handleSaveDiscount} className="space-y-4">
          <Input label="العام الدراسي" value={academicYear} disabled readOnly />

          {/* Type: fixed amount vs. percentage */}
          <div>
            <label className="block text-sm font-medium mb-1">نوع الخصم</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="discountType"
                  value="fixed"
                  checked={newDiscountType === "fixed"}
                  onChange={() => setNewDiscountType("fixed")}
                />
                مبلغ ثابت (ل.س)
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="discountType"
                  value="percentage"
                  checked={newDiscountType === "percentage"}
                  onChange={() => setNewDiscountType("percentage")}
                />
                نسبة مئوية (٪)
              </label>
            </div>
          </div>

          <Input
            label={newDiscountType === "percentage" ? "قيمة الخصم (٪)" : "قيمة الخصم (ل.س)"}
            type="number"
            value={newDiscountValue}
            onChange={(e) => setNewDiscountValue(e.target.value)}
            min="0"
            max={newDiscountType === "percentage" ? "100" : undefined}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={closeAddDiscountModal}>إلغاء</Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">حفظ الخصم</Button>
          </div>
        </form>
      </Modal>

      <FeeCalculatorModal
        isOpen={isCalculatorModalOpen}
        onClose={() => setIsCalculatorModalOpen(false)}
        schoolFees={allSchoolFees}
        totalPaid={totalPaidForCalculator}
        studentDiscount={studentDiscount}
        studentDiscountType={studentDiscountType}
        defaultClassState={autoStage}
      />

    </div>
  );
};
export default StudentDetailPage;
